import { AxDataTable } from "@/components/ax/dataTable/AxDataTable";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { ROUTES } from "@/constants/routeConstants";
import { Player } from "@/lib/types";
import { deletePlayer, getPlayer } from "@/sideEffects/api";
import { ColumnDef, ColumnSort } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectActiveFirm } from "@/store/appSelectors";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { AddRowButton } from "@/components/ax/dataTable/AddRowButton";
import { UpdateRowButton } from "@/components/ax/dataTable/UpdateRowButton";
import { SelectedMediaPreview } from "../shared/SelectedMediaPreview";
import { showSuccess, showApiError } from "@/lib/toast";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { FilterFormSchemaType, PlayerPageFilterForm } from "./PlayerPageFilterForm";
import { DashboardPlayerHealthStatus } from "../dashboard/DashboardPlayerHealthStatus";
import { PageLayout } from "@/components/PageLayout";
import { PermissionChecker2 } from "@/components/permission/PermissionChecker2";

export const PlayerPage = () => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const [sorting, setSorting] = useState<ColumnSort>();
  const [filterValues, setFilterValues] = useState<FilterFormSchemaType>({
    screenSizeId: "",
    tagId: "",
  });

  useBreadcrumbs([
    {
      name: t("entities.player"),
      path: ROUTES.PLAYER,
    },
  ]);

  const { goToPlayerDetail, goToPlayerAdd } = useAppNavigate();

  const columns: ColumnDef<Player>[] = [
    {
      accessorKey: "name",
      header: t("common.name"),
    },
    {
      accessorKey: "timezone",
      header: t("entities.timeZone"),
    },
    {
      accessorKey: "screenSizeName",
      header: t("entities.screenSize"),
    },
    {
      accessorKey: "code",
      header: t("licence.code"),
    },
    {
      // use this for uniq header purpose or table complains
      accessorKey: "firmLicenceintId",
      header: t("playerInfo.healthStatus"),
      cell: ({ row }) => {
        return <DashboardPlayerHealthStatus playerId={row.original.intId} />;
      },
    },
    {
      accessorKey: "manufacturer",
      header: t("common.manufacturer"),
    },
    {
      accessorKey: "defaultContentintId",
      header: () => t("content.defaultContent"),
      cell: ({ row }) => {
        return <SelectedMediaPreview contentIntId={row.original.defaultContentintId} />;
      },
    },
    {
      accessorKey: "id",
      header: () => t("common.media"),
      cell: ({ row }) => {
        return (
          <SelectedMediaPreview
            channelIntId={row.original.channelintId}
            contentGroupIntId={row.original.contentGroupintId}
            contentIntId={row.original.contentintId}
          />
        );
      },
    },
    {
      accessorKey: "id",
      header: () => "",
      cell: ({ row }) => {
        return (
          <PermissionChecker2 entity="Player" rights={["GetDetails"]}>
            <div className="flex items-center justify-end gap-2">
              <UpdateRowButton onClickUpdate={() => goToPlayerDetail(row.original.id)} />
            </div>
          </PermissionChecker2>
        );
      },
    },
  ];

  const queryClient = useQueryClient();
  const deletePlayersMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map((id) => deletePlayer(id, String(activeFirm?.firmGuid))));
    },
    onSuccess: () => {
      showSuccess(t("common.deletedSuccess"));
      queryClient.invalidateQueries({ queryKey: ["getPlayer"] });
    },
    onError: showApiError,
  });

  return (
    <PageLayout
      title={<>{t("entities.player")}</>}
      description={t("player.playerDesc")}
      extraRight={
        <PermissionChecker2 entity="Player" rights={["Add"]}>
          <AddRowButton title={t("common.addNewEntity", { entity: t("entities.player") })} onClickAdd={goToPlayerAdd} />
        </PermissionChecker2>
      }
    >
      <AxDataTable
        columns={columns}
        fetcher={getPlayer}
        getRowId={(row) => row.id.toString()}
        uniqQueryKey="getPlayer"
        dynamicRequestParams={{
          Firm_Id: String(activeFirm?.firmGuid),
          sortBy: sorting?.id,
          sortDirection: sorting?.desc ? "desc" : "asc",
          ScreenSizeGuid: filterValues.screenSizeId,
          TagGuid: filterValues.tagId,
        }}
        extraHeader={<PlayerPageFilterForm onChange={setFilterValues} />}
        onDeleteRows={deletePlayersMutation.mutateAsync}
        deleting={deletePlayersMutation.isPending}
        sortableColumns={["name", "timezone", "code", "manufacturer"]}
        onClickSort={(sorting) => {
          if (sorting && sorting.length) {
            const sort = sorting[0];
            setSorting(sort);
          } else {
            setSorting(undefined);
          }
        }}
        exportableColumns={["name", "timezone", "screenSizeName", "code", "manufacturer"]}
      />
    </PageLayout>
  );
};
